import { t as translate } from 'i18n';
import { BAR_RADIUS, calloutMark, referenceLines, selectedTicks, toneOf } from './chartParts';
import { formatTick } from './units';

/**
 * Pure trend card → ECharts option. `view` is live: the axis reads its width
 * and selection while it lays out, so a hover redraws without React. SIDE is
 * the figure's margin; the card's own padding absorbs it (TrendCard).
 */
export const SIDE = 12;
const TOP = 2;
const BOTTOM = 32;
// Pixels held back past the bars for the callout: arrow, body and a little air.
const HEADROOM = 34;
// A partial period reads paler; the selected one less so, to keep its accent.
const PARTIAL_OPACITY = 0.45;
const PARTIAL_SELECTED_OPACITY = 0.7;

/** The y range, with pixels held back for the callout past either end. */
function valueRange(values, plotHeight) {
  const top = Math.max(...values, 0);
  const low = Math.min(...values, 0);
  const padTop = top > 0 ? HEADROOM : 0;
  const padLow = low < 0 ? HEADROOM : 0;
  const span = (top - low || 1) / (1 - (padTop + padLow) / plotHeight);
  return [low - (padLow * span) / plotHeight, top + (padTop * span) / plotHeight];
}

function barSeries({ card, view, tone, negative, theme, compact, values }) {
  const diverging = values.some((value) => value !== null && value < 0);
  return {
    id: 'trend', type: 'bar', barCategoryGap: compact ? '35%' : '42%', barMaxWidth: 24, emphasis: { disabled: true },
    data: card.buckets.map((bucket, index) => {
      const below = bucket.value !== null && bucket.value < 0;
      const accent = below ? negative.colour : tone.colour;
      const soft = below ? negative.soft : diverging ? tone.soft : theme.chart.neutral.bar;
      return {
        value: bucket.value,
        itemStyle: {
          color: index === view.selected ? accent : soft,
          opacity: !bucket.partial ? 1 : index === view.selected ? PARTIAL_SELECTED_OPACITY : PARTIAL_OPACITY,
          borderRadius: below ? [0, 0, BAR_RADIUS, BAR_RADIUS] : [BAR_RADIUS, BAR_RADIUS, 0, 0],
        },
      };
    }),
  };
}

/** A solid line over whole periods; a paler tail joins it for a partial one. */
function lineSeries({ card, tone }) {
  const { buckets } = card;
  const solid = buckets.map((bucket) => (bucket.partial ? null : bucket.value));
  const dashed = (bucket) => bucket?.partial && bucket.value !== null;
  const tail = buckets.map((bucket, index) => (bucket.partial || dashed(buckets[index + 1]) || dashed(buckets[index - 1]) ? bucket.value : null));
  const base = { type: 'line', showSymbol: true, showAllSymbol: true, symbol: 'none', connectNulls: false, emphasis: { disabled: true }, silent: true };
  // A value between two gaps has no segment to draw: only a dot can show it.
  const dotted = (values, opacity) => values.map((value, index) => (value !== null && (values[index - 1] ?? null) === null && (values[index + 1] ?? null) === null
    ? { value, symbol: 'circle', symbolSize: 7, itemStyle: { color: tone.colour, opacity } } : value));
  return [
    { ...base, id: 'trend', data: dotted(solid, 1), lineStyle: { color: tone.colour, width: 2 }, areaStyle: { color: tone.colour, opacity: 0.1 } },
    ...(card.partial ? [{ ...base, id: 'trend-partial', data: dotted(tail, PARTIAL_OPACITY), lineStyle: { color: tone.colour, width: 2, opacity: PARTIAL_OPACITY, type: [4, 3] } }] : []),
  ];
}

export function buildTrendOption({ card, view, kind, tone: toneName, theme: tokens, height, compact }) {
  const theme = { ...tokens, type: tokens.chart.type };
  const { chart, fontFamily: font, ink, border } = theme;
  const tone = toneOf(chart.tones, toneName);
  const negative = chart.tones.rose;
  const values = card.buckets.map((bucket) => bucket.value);
  const averageLabel = translate('assistant.trendCardAverageShort');
  const lines = [{ value: 0, colour: border, dashed: false }];
  if (card.average !== null) lines.push({ value: card.average, label: compact ? averageLabel : `${averageLabel} ${formatTick(card.average, card.unit)}` });
  if (card.target) lines.push({ value: card.target.value, label: card.target.label, colour: tone.colour });
  const known = [...values.filter((value) => value !== null), ...(card.target ? [card.target.value] : [])];
  const [min, max] = valueRange(known, height - TOP - BOTTOM);
  const parts = { card, view, tone, negative, theme, compact, values };
  const series = kind === 'line_card' ? lineSeries(parts) : [barSeries(parts)];
  const value = values[view.selected];
  series[0].markLine = referenceLines(theme, lines);
  series[0].markPoint = calloutMark({ theme, accent: value !== null && value < 0 ? negative : tone, index: view.selected, count: values.length, value, unit: card.unit, view, left: SIDE, right: SIDE });
  const ticks = card.buckets.map((bucket) => bucket.tick);
  return {
    animation: false,
    textStyle: { fontFamily: font, color: ink },
    grid: { left: SIDE, right: SIDE, top: TOP, bottom: BOTTOM },
    xAxis: {
      type: 'category', boundaryGap: true, data: ticks,
      axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false }, axisPointer: { show: false },
      axisLabel: selectedTicks({ ticks, view, theme, accent: tone, left: SIDE, right: SIDE }),
    },
    yAxis: { type: 'value', show: false, min, max },
    series,
  };
}
